import type { JournalSortData, Geo as EncodedGeo, ArticlesData } from '@/shared/api/experience/types.gen';

import type { ArticleInList, Hashtag } from '@/shared/models/articleInList';

/**
 * Преобразует Geo-объект, полученный из API в список хэштегов.
 *
 * @param encoded значение из API
 * @returns внутренне представление
 */
const normalizeHashtags = (encoded: EncodedGeo): Hashtag[] => {
  const { country } = encoded;

  if (!country) {
    return [];
  }

  const hashtags = [];
  let counter = 0;

  for (let i = 0; counter < 3 && i < country.length; ++i) {
    const tag = country[i];

    if (tag.name && tag.slug) {
      hashtags.push({ name: tag.name, slug: tag.slug });
      counter += 1;
    }
  }

  return hashtags;
};

const readStringField = (source: Record<string, unknown>, fieldName: string): string => {
  const rawValue = source[fieldName];
  return typeof rawValue === 'string' ? rawValue : '';
};

const resolvePhotoUrl = (encoded: JournalSortData | ArticlesData): string => {
  if (encoded.lightbox_photo_url) {
    return encoded.lightbox_photo_url;
  }

  const encodedRecord = encoded as unknown as Record<string, unknown>;
  const mainPhotoRaw = encodedRecord.main_photo;
  if (!mainPhotoRaw || typeof mainPhotoRaw !== 'object') {
    return readStringField(encodedRecord, 'photo');
  }

  const mainPhoto = mainPhotoRaw as Record<string, unknown>;
  return (
    readStringField(mainPhoto, 'scaled') ||
    readStringField(mainPhoto, 'thumbnail_l') ||
    readStringField(mainPhoto, 'lightbox') ||
    readStringField(mainPhoto, 'thumbnail')
  );
};

/**
 * Преобразует объект, полученный из API в объект "Статья в списке".
 *
 * @param encoded значение из API
 * @returns внутреннее представление
 */
export const normalizeArticleInList = (encoded: JournalSortData | ArticlesData): ArticleInList => {
  const normalizedPhotoUrl = resolvePhotoUrl(encoded);

  return {
    id: encoded.id,
    photo: normalizedPhotoUrl,
    publishedAt: encoded.published_at ? new Date(encoded.published_at) : new Date(),
    readingTime: encoded.reading_time,
    slug: encoded.slug ?? '',
    tagline: encoded.tagline ?? '',
    intro: encoded.intro ?? '',
    title: encoded.title,
    type: encoded.type,
    hashtags: encoded.geo ? normalizeHashtags(encoded.geo) : [],
    url: encoded.url ?? '',
    lightboxPhotoUrl: normalizedPhotoUrl,
    isUgc: encoded.is_ugc ?? false,
  };
};
